    // src/data.js

    import { makeResources, timesResources, addResources, roundResources, ceilResources } from "./resources";
    import { getRandomPlanetName } from './utils/planetNames';

    export const defaultProd = makeResources(20, 10, 0, 0);

    // Declarative requirements, replacing opaque available(state, planetId)
    // boolean predicates. Every requirement in this codebase is a simple
    // AND of "building level >= N" (on the planet being checked) and
    // "research level >= N" (empire-wide) — see plan.md §3. This lets the
    // planner introspect *why* something is blocked, not just get true/false.
    export const meetsRequirement = (state, planetId, req) => {
        switch (req.kind) {
            case 'building':
                return state.planets[planetId].buildings[req.id] >= req.level;
            case 'research':
                return state.research[req.id] >= req.level;
            default:
                return false;
        }
    };

    export const meetsRequirements = (state, planetId, requires) =>
        requires.every((req) => meetsRequirement(state, planetId, req));

    // Called as entry.available(state, planetId) with entry as receiver, so
    // `this.requires` resolves to the sibling `requires` array.
    const availableFromRequirements = function (state, planetId) {
        return meetsRequirements(state, planetId, this.requires);
    };

    export const makeDefaultPlanet = (name, imageName = null) => {
        return {
            name: name,
            imageName: imageName,
            temperature: 50.0,
            size: 14422,
            time: 0, // For GameReducer state
            index: 0, // For GameReducer state
            resources: { metal: 2000, crystal: 100, deuterium: 0, energy: 0 },
            buildAction: undefined,
            buildings: {
                metalMine: 0,
                crystalMine: 0,
                deuteriumSynthesizer: 0,
                solarPlant: 0,
                fusionReactor: 0,
                robotFactory: 0,
                naniteFactory: 0,
                hangar: 0,
                researchLab: 0,
                metalStorage: 0,
                crystalStorage: 0,
                deutStorage: 0,
                repairDock: 0,
                silo: 0,
            },
            fleetActions: [],
            shipActions: [],
            ships: {
                smallCargo: 0,
                bigCargo: 0,
                lightFighter: 0,
                heavyFighter: 0,
                cruiser: 0,
                battleShip: 0,
                colonyShip: 0,
                recycler: 0,
                spyProbe: 0,
                bomber: 0,
                solarSatelite: 0,
                starFighter: 0,
                battleCruiser: 0,
                rip: 0,
                stormTransporter: 0,
            },
            defenses: {
                rocketLauncher: 0,
                lightLaser: 0,
                heavyLaser: 0,
                gaussCannon: 0,
                ionCannon: 0,
                plasmaTurret: 0,
                smallShieldDome: 0,
                largeShieldDome: 0,
                antiBallisticMissile: 0,
                interplanetaryMissile: 0,
            },
            productionFactors : {
                metalMine: 1.0,
                crystalMine: 1.0,
                deuteriumSynthesizer: 1.0,
                solarPlant: 1.0,
                fusionReactor: 1.0,
                solarSatelite: 1.0,
            }
        }
    }

    export const defaultInitialState = {
        version: 2,
        startTime: new Date('2026-07-31T18:00:00+02:00'), // German time (CEST)
        time: 0,
        // v0.2.1 hotfix uni settings (see commit 9e5abc4): the current
        // admin-configured universe runs at 4x resources, 2x build/research.
        ressSpeed: 4.0,
        buildSpeed: 2.0,
        researchSpeed: 2.0,
        planets: {
            1: {
                ...makeDefaultPlanet("Home planet", getRandomPlanetName()),
                // v0.2.1 hotfix uni settings: start resources 5000/1500/0.
                // Only overridden for the home planet here since colony
                // starting resources (via makeDefaultPlanet in
                // App.js's handleAddPlanet) haven't been confirmed to match.
                resources: { metal: 5000, crystal: 1500, deuterium: 0, energy: 0 },
            },
        },
        researchAction: undefined,
        research: {
            spioTech: 0,
            compTech: 0,
            weaponTech: 0,
            shieldTech: 0,
            armourTech: 0,
            energyTech: 0,
            hyperTech: 0,
            combustionEngine: 0,
            impulseEngine: 0,
            hyperEngine: 0,
            laserTech: 0,
            ionTech: 0,
            plasmaTech: 0,
            igrnTech: 0,
            astroTech: 0,
            metTech: 0,
            crysTech: 0,
            deutTech: 0,
            graviton: 0,
        }
    }

    export const buildingData = {
        metalMine: {cost: (level) => { return {
                        metal: Math.round(40.0 * Math.pow(1.5, level)),
                        crystal: Math.round(10.0 * Math.pow(1.5, level)),
                        deuterium: 0,
                        energy: 0}},
                    production: (level, state, planetId) => { 
                        const tech = state.research.metTech;
                        return {
                        metal: ((30.0 * level * Math.pow(1.1, level)) * state.ressSpeed),
                        crystal: 0,
                        deuterium: 0,
                        // Energy is deliberately *not* scaled by RESS_SPEED --
                        // matches the real server, where energy production/
                        // consumption is governed by a separate
                        // energy_multiplier, decoupled from resource_multiplier
                        // (confirmed against src/Core/ResourceUpdate.php).
                        energy: - (10.0 * level * Math.pow(1.1, level))}},
                    name: "Metal mine",
                    requires: [],
                    available: availableFromRequirements,
                    },
        crystalMine: {cost: (level) => { return {
                        metal: Math.round(30.0 * Math.pow(1.6, level)),
                        crystal: Math.round(15.0 * Math.pow(1.6, level)),
                        deuterium: 0,
                        energy: 0}},
                    production: (level, state, planetId) => { 
                        const tech = state.research.crysTech;
                        return {
                        metal: 0,
                        crystal: ((20.0 * level * Math.pow(1.1, level)) * state.ressSpeed),
                        deuterium: 0,
                        energy: - (10.0 * level * Math.pow(1.1, level))}}, // not scaled -- see metalMine's comment
                    name: "Crystal mine",
                    requires: [],
                    available: availableFromRequirements,
                    },
        deuteriumSynthesizer: {cost: (level) => { return {
                        metal: Math.round(150.0 * Math.pow(1.5, level)),
                        crystal: Math.round(50.0 * Math.pow(1.5, level)),
                        deuterium: 0,
                        energy: 0}},
                    production: (level, state, planetId) => { 
                        const tech = state.research.deutTech;
                        const temp = state.planets[planetId].temperature;
                        return {
                        metal: 0,
                        crystal: 0,
                        deuterium: (Math.floor(10.0 * level * Math.pow(1.1, level)) * (1.44 - 0.004 * temp) * state.ressSpeed),
                        energy: - (20.0 * level * Math.pow(1.1, level))}}, // not scaled -- see metalMine's comment
                    name: "Deut synth.",
                    requires: [],
                    available: availableFromRequirements,
                    },
        solarPlant: {cost: (level) => { return {
                        metal: Math.round(50.0 * Math.pow(1.5, level)),
                        crystal: Math.round(20.0 * Math.pow(1.5, level)),
                        deuterium: 0,
                        energy: 0}},
                    production: (level, state, planetId) => { return {
                        metal: 0,
                        crystal: 0,
                        deuterium: 0,
                        energy: + (20.0 * level * Math.pow(1.1, level))}}, // not scaled -- see metalMine's comment
                    name: "Solar",
                    requires: [],
                    available: availableFromRequirements,
                    },
        fusionReactor: {cost: (level) => { return {
                        metal: Math.floor(500.0 * Math.pow(1.8, level)),
                        crystal: Math.floor(200.0 * Math.pow(1.8, level)),
                        deuterium: Math.floor(100.0 * Math.pow(1.8, level)),
                        energy: 0}},
                    production: (level, state, planetId) => { 
                        const tech = state.research.energyTech;
                        return {
                            metal: 0,
                            crystal: 0,
                            // Deuterium *is* scaled (it's a resource-bucket
                            // transaction, same as deuteriumSynthesizer's
                            // output); energy is not -- see metalMine's comment.
                            deuterium: - ((10.0 * level * Math.pow(1.1, level)) * state.ressSpeed),
                            energy: + (30.0 * level * Math.pow(1.05 + 0.01 * tech, level))}},
                    name: "Fusion reactor",
                    requires: [
                        { kind: 'building', id: 'deuteriumSynthesizer', level: 5 },
                        { kind: 'research', id: 'energyTech', level: 3 },
                    ],
                    available: availableFromRequirements,
                    },
        robotFactory: {cost: (level) => { return {
                        metal: Math.round(200.0 * Math.pow(2, level)),
                        crystal: Math.round(60.0 * Math.pow(2, level)),
                        deuterium: Math.round(100.0 * Math.pow(2, level)),
                        energy: 0}},
                    production: makeResources,
                    name: "Robot factory",
                    requires: [],
                    available: availableFromRequirements,
                    },
        naniteFactory: {cost: (level) => { return {
                        metal: Math.round(500000.0 * Math.pow(2, level)),
                        crystal: Math.round(250000.0 * Math.pow(2, level)),
                        deuterium: Math.round(50000.0 * Math.pow(2, level)),
                        energy: 0}},
                    production: makeResources,
                    name: "Nanite factory",
                    requires: [
                        { kind: 'building', id: 'robotFactory', level: 10 },
                        { kind: 'research', id: 'compTech', level: 10 },
                    ],
                    available: availableFromRequirements,
                    },
        researchLab: {cost: (level) => { return {
                        metal: Math.round(100.0 * Math.pow(2, level)),
                        crystal: Math.round(200.0 * Math.pow(2, level)),
                        deuterium: Math.round(100.0 * Math.pow(2, level)),
                        energy: 0}},
                    production: makeResources,
                    name: "Research Lab",
                    requires: [],
                    available: availableFromRequirements,
                    },
        hangar: {cost: (level) => { return {
                        metal: Math.round(200.0 * Math.pow(2, level)),
                        crystal: Math.round(100.0 * Math.pow(2, level)),
                        deuterium: Math.round(50.0 * Math.pow(2, level)),
                        energy: 0}},
                    production: makeResources,
                    name: "Hangar",
                    requires: [
                        { kind: 'building', id: 'robotFactory', level: 2 },
                    ],
                    available: availableFromRequirements,
                    },
        metalStorage: {cost: (level) => { return {
                        metal: Math.round(500.0 * Math.pow(2, level)),
                        crystal: 0,
                        deuterium: 0,
                        energy: 0}},
                    production: makeResources,
                    name: "Metal storage",
                    requires: [],
                    available: availableFromRequirements,
                    },
        crystalStorage: {cost: (level) => { return {
                        metal: Math.round(500.0 * Math.pow(2, level)),
                        crystal: Math.round(250.0 * Math.pow(2, level)),
                        deuterium: 0,
                        energy: 0}},
                    production: makeResources,
                    name: "Crystal storage",
                    requires: [],
                    available: availableFromRequirements,
                    },
        deutStorage: {cost: (level) => { return {
                        metal: Math.round(500.0 * Math.pow(2, level)),
                        crystal: Math.round(250.0 * Math.pow(2, level)),
                        deuterium: 0.0,
                        energy: 0}},
                    production: makeResources,
                    name: "Deut. tank",
                    requires: [],
                    available: availableFromRequirements,
                    },
        repairDock: {cost: (level) => { return {
                        metal: Math.round(10000.0 * Math.pow(2, level)),
                        crystal: Math.round(1000.0 * Math.pow(2, level)),
                        deuterium: Math.round(1500.0 * Math.pow(2, level)),
                        energy: 0}},
                    production: makeResources,
                    name: "Repair dock",
                    requires: [],
                    available: availableFromRequirements,
                    },
        silo: {cost: (level) => { return {
                        metal: Math.round(10000.0 * Math.pow(2, level)),
                        crystal: Math.round(10000.0 * Math.pow(2, level)),
                        deuterium: Math.round(500.0 * Math.pow(2, level)),
                        energy: 0}},
                    production: makeResources,
                    name: "Silo",
                    requires: [],
                    available: availableFromRequirements,
                    },
      };

      export const researchData = {
        spioTech: {cost: (level) => { return {
            metal: Math.round(100.0 * Math.pow(2, level)),
            crystal: Math.round(500.0 * Math.pow(2, level)),
            deuterium: Math.round(100.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Spio tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 3 },
            ],
            available: availableFromRequirements,
            },
        compTech: {cost: (level) => { return {
            metal: 0,
            crystal: Math.round(200.0 * Math.pow(2, level)),
            deuterium: Math.round(300.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Comp. Tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 1 },
            ],
            available: availableFromRequirements,
            },
        weaponTech: {cost: (level) => { return {
            metal: Math.round(800.0 * Math.pow(2, level)),
            crystal: Math.round(200.0 * Math.pow(2, level)),
            deuterium: 0,
            energy: 0}},
            name: "Weapon tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 4 },
            ],
            available: availableFromRequirements,
            },
        shieldTech: {cost: (level) => { return {
            metal: Math.round(100.0 * Math.pow(2, level)),
            crystal: Math.round(300.0 * Math.pow(2, level)),
            deuterium: Math.round(0.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Shield Tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 6 },
                { kind: 'research', id: 'energyTech', level: 3 },
            ],
            available: availableFromRequirements,
            },
        armourTech: {cost: (level) => { return {
            metal: Math.round(500.0 * Math.pow(2, level)),
            crystal: 0,
            deuterium: 0,
            energy: 0}},
            name: "Armour Tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 2 },
            ],
            available: availableFromRequirements,
            },
        energyTech: {cost: (level) => { return {
            metal: 0,
            crystal: Math.round(400.0 * Math.pow(2, level)),
            deuterium: Math.round(200.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Energy Tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 1 },
            ],
            available: availableFromRequirements,
            },
        hyperTech: {cost: (level) => { return {
            metal: Math.round(0.0 * Math.pow(2, level)),
            crystal: Math.round(2000.0 * Math.pow(2, level)),
            deuterium: Math.round(1000.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Hyper Tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 7 },
                { kind: 'research', id: 'energyTech', level: 5 },
                { kind: 'research', id: 'shieldTech', level: 5 },
            ],
            available: availableFromRequirements,
            },
        combustionEngine: {cost: (level) => { return {
            metal: Math.round(200.0 * Math.pow(2, level)),
            crystal: 0,
            deuterium: Math.round(300.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Combustion Engine",
            requires: [
                { kind: 'building', id: 'researchLab', level: 1 },
                { kind: 'research', id: 'energyTech', level: 1 },
            ],
            available: availableFromRequirements,
            },
        impulseEngine: {cost: (level) => { return {
            metal: Math.round(1000.0 * Math.pow(2, level)),
            crystal: Math.round(2000.0 * Math.pow(2, level)),
            deuterium: Math.round(300.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Impulse Engine",
            requires: [
                { kind: 'building', id: 'researchLab', level: 2 },
                { kind: 'research', id: 'energyTech', level: 1 },
            ],
            available: availableFromRequirements,
            },
        hyperEngine: {cost: (level) => { return {
            metal: Math.round(5000.0 * Math.pow(2, level)),
            crystal: Math.round(10000.0 * Math.pow(2, level)),
            deuterium: Math.round(3000.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Hyper Engine",
            requires: [
                { kind: 'research', id: 'hyperTech', level: 3 },
            ],
            available: availableFromRequirements,
            },
        laserTech: {cost: (level) => { return {
            metal: Math.round(100.0 * Math.pow(2, level)),
            crystal: Math.round(50.0 * Math.pow(2, level)),
            deuterium: 0,
            energy: 0}},
            name: "Laser Tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 1 },
                { kind: 'research', id: 'energyTech', level: 2 },
            ],
            available: availableFromRequirements,
            },
        ionTech: {cost: (level) => { return {
            metal: Math.round(500.0 * Math.pow(2, level)),
            crystal: Math.round(150.0 * Math.pow(2, level)),
            deuterium: Math.round(50.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Ion Tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 4 },
                { kind: 'research', id: 'energyTech', level: 4 },
                { kind: 'research', id: 'laserTech', level: 5 },
            ],
            available: availableFromRequirements,
            },
        plasmaTech: {cost: (level) => { return {
            metal: Math.round(1000.0 * Math.pow(2, level)),
            crystal: Math.round(2000.0 * Math.pow(2, level)),
            deuterium: Math.round(500.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Plasma Tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 4 },
                { kind: 'research', id: 'energyTech', level: 8 },
                { kind: 'research', id: 'laserTech', level: 10 },
                { kind: 'research', id: 'ionTech', level: 5 },
            ],
            available: availableFromRequirements,
            },
        igrnTech: {cost: (level) => { return {
            metal: Math.round(120000.0 * Math.pow(2, level)),
            crystal: Math.round(200000.0 * Math.pow(2, level)),
            deuterium: Math.round(80000.0 * Math.pow(2, level)),
            energy: 0}},
            name: "IGRN",
            requires: [
                { kind: 'building', id: 'researchLab', level: 10 },
                { kind: 'research', id: 'compTech', level: 8 },
                { kind: 'research', id: 'hyperTech', level: 8 },
            ],
            available: availableFromRequirements,
            },
        astroTech: {cost: (level) => { return {
            metal: 100 * Math.floor(0.5 + 40 * Math.pow(1.75, level - 1)),
            crystal: 100 * Math.floor(0.5 + 80 * Math.pow(1.75, level - 1)),
            deuterium: 100 * Math.floor(0.5 + 40 * Math.pow(1.75, level - 1)),
            energy: 0}},
            name: "Astro tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 3 },
                { kind: 'research', id: 'spioTech', level: 3 },
                { kind: 'research', id: 'impulseEngine', level: 3 },
            ],
            available: availableFromRequirements,
            },
        metTech: {cost: (level) => { return {
            metal: Math.round(375.0 * Math.pow(2, level)),
            crystal: Math.round(250.0 * Math.pow(2, level)),
            deuterium: Math.round(125.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Met Tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 4 },
                { kind: 'research', id: 'energyTech', level: 3 },
            ],
            available: availableFromRequirements,
            },
        crysTech: {cost: (level) => { return {
            metal: Math.round(1000.0 * Math.pow(2, level)),
            crystal: Math.round(750.0 * Math.pow(2, level)),
            deuterium: Math.round(500.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Crys. Tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 4 },
                { kind: 'research', id: 'energyTech', level: 3 },
            ],
            available: availableFromRequirements,
            },
        deutTech: {cost: (level) => { return {
            metal: Math.round(1250.0 * Math.pow(2, level)),
            crystal: Math.round(1000.0 * Math.pow(2, level)),
            deuterium: Math.round(750.0 * Math.pow(2, level)),
            energy: 0}},
            name: "Deut. Tech.",
            requires: [
                { kind: 'building', id: 'researchLab', level: 4 },
                { kind: 'research', id: 'energyTech', level: 3 },
            ],
            available: availableFromRequirements,
            },
        graviton: {cost: (level) => { return {
            metal: Math.round(0 * Math.pow(2, level)),
            crystal: Math.round(0 * Math.pow(2, level)),
            deuterium: Math.round(0 * Math.pow(2, level)),
            energy: 300000}},
            name: "Graviton",
            requires: [
                { kind: 'building', id: 'researchLab', level: 12 },
            ],
            available: availableFromRequirements,
            },
      }

      export const shipData = {
        smallCargo: {cost: (level) => { return makeResources(2000, 2000, 0)},
            name: "Light Cargo",
            requires: [
                { kind: 'building', id: 'hangar', level: 2 },
                { kind: 'research', id: 'combustionEngine', level: 2 },
            ],
            available: availableFromRequirements,
            },
        bigCargo: {cost: (level) => { return makeResources(6000, 6000, 0)},
            name: "Heavy Cargo",
            requires: [
                { kind: 'building', id: 'hangar', level: 4 },
                { kind: 'research', id: 'combustionEngine', level: 6 },
            ],
            available: availableFromRequirements,
            },
        lightFighter: {cost: (level) => { return makeResources(3000, 1000, 0)},
            name: "Light Fighter",
            requires: [
                { kind: 'building', id: 'hangar', level: 1 },
                { kind: 'research', id: 'combustionEngine', level: 1 },
            ],
            available: availableFromRequirements,
            },
        heavyFighter: {cost: (level) => { return makeResources(6000, 4000, 0)},
            name: "Heavy Fighter",
            requires: [
                { kind: 'building', id: 'hangar', level: 3 },
                { kind: 'research', id: 'armourTech', level: 2 },
                { kind: 'research', id: 'impulseEngine', level: 2 },
            ],
            available: availableFromRequirements,
            },
        cruiser: {cost: (level) => { return makeResources(20000, 7000, 2000)},
            name: "Cruiser",
            requires: [
                { kind: 'building', id: 'hangar', level: 5 },
                { kind: 'research', id: 'impulseEngine', level: 4 },
                { kind: 'research', id: 'ionTech', level: 2 },
            ],
            available: availableFromRequirements,
            },
        battleShip: {cost: (level) => { return makeResources(45000, 15000, 0)},
            name: "Battleship",
            requires: [
                { kind: 'building', id: 'hangar', level: 7 },
                { kind: 'research', id: 'hyperEngine', level: 4 },
            ],
            available: availableFromRequirements,
            },
        colonyShip: {cost: (level) => { return makeResources(10000, 20000, 10000)},
            name: "Colony Ship",
            requires: [
                { kind: 'building', id: 'hangar', level: 4 },
                { kind: 'research', id: 'impulseEngine', level: 3 },
            ],
            available: availableFromRequirements,
            },
        recycler: {cost: (level) => { return makeResources(10000, 6000, 2000)},
            name: "Recycler",
            requires: [
                { kind: 'building', id: 'hangar', level: 4 },
                { kind: 'research', id: 'combustionEngine', level: 6 },
                { kind: 'research', id: 'shieldTech', level: 2 },
            ],
            available: availableFromRequirements,
            },
        spyProbe: {cost: (level) => { return makeResources(0, 1000, 0)},
            name: "Spy Probe",
            requires: [
                { kind: 'building', id: 'hangar', level: 3 },
                { kind: 'research', id: 'combustionEngine', level: 3 },
                { kind: 'research', id: 'spioTech', level: 2 },
            ],
            available: availableFromRequirements,
            },
        bomber: {cost: (level) => { return makeResources(50000, 25000, 15000)},
            name: "Bomber",
            requires: [
                { kind: 'building', id: 'hangar', level: 8 },
                { kind: 'research', id: 'impulseEngine', level: 6 },
                { kind: 'research', id: 'plasmaTech', level: 5 },
            ],
            available: availableFromRequirements,
            },
        solarSatelite:{cost: (level) => { return makeResources(0, 2000, 500)},
            name: "Solar Satellite",
            requires: [
                { kind: 'building', id: 'hangar', level: 1 },
            ],
            available: availableFromRequirements,
            },
        starFighter:{cost: (level) => { return makeResources(60000, 50000, 15000)},
            name: "Destroyer",
            requires: [
                { kind: 'building', id: 'hangar', level: 9 },
                { kind: 'research', id: 'hyperEngine', level: 6 },
                { kind: 'research', id: 'hyperTech', level: 5 },
            ],
            available: availableFromRequirements,
            },
        battleCruiser:{cost: (level) => { return makeResources(30000, 40000, 15000)},
            name: "Battle Cruiser",
            requires: [
                { kind: 'building', id: 'hangar', level: 8 },
                { kind: 'research', id: 'laserTech', level: 12 },
                { kind: 'research', id: 'hyperEngine', level: 5 },
                { kind: 'research', id: 'hyperTech', level: 5 },
            ],
            available: availableFromRequirements,
            },
        rip:{cost: (level) => { return makeResources(5000000, 4000000, 1000000)},
            name: "RIP",
            requires: [
                { kind: 'building', id: 'hangar', level: 12 },
                { kind: 'research', id: 'graviton', level: 1 },
                { kind: 'research', id: 'hyperEngine', level: 7 },
                { kind: 'research', id: 'hyperTech', level: 6 },
            ],
            available: availableFromRequirements,
            },
        stormTransporter: {cost: (level) => { return makeResources(7500, 2500, 0)},
            name: "Storm Transporter",
            requires: [
                { kind: 'building', id: 'hangar', level: 4 },
                { kind: 'research', id: 'armourTech', level: 4 },
                { kind: 'research', id: 'combustionEngine', level: 5 },
            ],
            available: availableFromRequirements,
            },
      }

      // Built in the same shipyard queue as shipData (see engine/handlers/buildDefense.js) —
      // flat cost like ships, no per-unit-owned scaling.
      export const defenseData = {
        rocketLauncher: {cost: (level) => { return makeResources(2000, 0, 0)},
            name: "Rocket Launcher",
            requires: [
                { kind: 'building', id: 'hangar', level: 1 },
            ],
            available: availableFromRequirements,
            },
        lightLaser: {cost: (level) => { return makeResources(1500, 500, 0)},
            name: "Light Laser",
            requires: [
                { kind: 'building', id: 'hangar', level: 2 },
                { kind: 'research', id: 'energyTech', level: 1 },
                { kind: 'research', id: 'laserTech', level: 3 },
            ],
            available: availableFromRequirements,
            },
        heavyLaser: {cost: (level) => { return makeResources(6000, 2000, 0)},
            name: "Heavy Laser",
            requires: [
                { kind: 'building', id: 'hangar', level: 4 },
                { kind: 'research', id: 'energyTech', level: 3 },
                { kind: 'research', id: 'laserTech', level: 6 },
            ],
            available: availableFromRequirements,
            },
        gaussCannon: {cost: (level) => { return makeResources(20000, 15000, 2000)},
            name: "Gauss Cannon",
            requires: [
                { kind: 'building', id: 'hangar', level: 6 },
                { kind: 'research', id: 'energyTech', level: 6 },
                { kind: 'research', id: 'weaponTech', level: 3 },
                { kind: 'research', id: 'shieldTech', level: 1 },
            ],
            available: availableFromRequirements,
            },
        ionCannon: {cost: (level) => { return makeResources(2000, 6000, 0)},
            name: "Ion Cannon",
            requires: [
                { kind: 'building', id: 'hangar', level: 4 },
                { kind: 'research', id: 'ionTech', level: 4 },
            ],
            available: availableFromRequirements,
            },
        plasmaTurret: {cost: (level) => { return makeResources(50000, 50000, 30000)},
            name: "Plasma Turret",
            requires: [
                { kind: 'building', id: 'hangar', level: 8 },
                { kind: 'research', id: 'plasmaTech', level: 7 },
            ],
            available: availableFromRequirements,
            },
        smallShieldDome: {cost: (level) => { return makeResources(10000, 10000, 0)},
            name: "Small Shield Dome",
            requires: [
                { kind: 'building', id: 'hangar', level: 1 },
                { kind: 'research', id: 'shieldTech', level: 2 },
            ],
            available: availableFromRequirements,
            },
        largeShieldDome: {cost: (level) => { return makeResources(50000, 50000, 0)},
            name: "Large Shield Dome",
            requires: [
                { kind: 'building', id: 'hangar', level: 6 },
                { kind: 'research', id: 'shieldTech', level: 6 },
            ],
            available: availableFromRequirements,
            },
        antiBallisticMissile: {cost: (level) => { return makeResources(8000, 0, 2000)},
            name: "Anti-Ballistic Missile",
            requires: [
                { kind: 'building', id: 'hangar', level: 1 },
                { kind: 'building', id: 'silo', level: 2 },
            ],
            available: availableFromRequirements,
            },
        interplanetaryMissile: {cost: (level) => { return makeResources(12500, 2500, 10000)},
            name: "Interplanetary Missile",
            requires: [
                { kind: 'building', id: 'hangar', level: 1 },
                { kind: 'building', id: 'silo', level: 4 },
                { kind: 'research', id: 'impulseEngine', level: 1 },
            ],
            available: availableFromRequirements,
            },
      }

      export const actionTypes = {
        UPGRADE_BUILDING: 'UPGRADE_BUILDING',
        RESEARCH: 'RESEARCH',
        BUILD_SHIP: 'BUILD_SHIP',
        BUILD_DEFENSE: 'BUILD_DEFENSE',
        SET_PROD: 'SET_PROD',
        WAIT: 'WAIT',
        TRANSPORT: 'TRANSPORT',
        // More action types can be added as needed
      };
    
  
  export const calcProduction = (state, planetId) => {
    const planet = state.planets[planetId];
    let prod = makeResources();
    prod = addResources(prod, timesResources(buildingData.metalMine.production(planet.buildings.metalMine, state, planetId), planet.productionFactors.metalMine));
    prod = addResources(prod, timesResources(buildingData.deuteriumSynthesizer.production(planet.buildings.deuteriumSynthesizer, state, planetId), planet.productionFactors.deuteriumSynthesizer));
    prod = addResources(prod, timesResources(buildingData.crystalMine.production(planet.buildings.crystalMine, state, planetId), planet.productionFactors.crystalMine));
    const energyConsumption = prod.energy;
    const energyBySolar = timesResources(buildingData.solarPlant.production(planet.buildings.solarPlant, state, planetId), planet.productionFactors.solarPlant).energy;
    
    // Add solar satelite production
    const energy_per_sat = (planetId == 1 ? 35 : (planet.temperature + 160) / 6.0 ); // Quirky pr0game settings
    const sat_energy = (energy_per_sat * planet.ships.solarSatelite);
    // Add fusion power
    let fusion = timesResources(buildingData.fusionReactor.production(planet.buildings.fusionReactor, state, planetId), planet.productionFactors.fusionReactor);
    const fusion_energy = fusion.energy;
    const energy_pos = Math.round(energyBySolar + fusion_energy + sat_energy);
    prod.energy += energy_pos;
    if(prod.energy < 0)
    {
        const actualEnergy = prod.energy;
        prod = timesResources(prod, energy_pos / Math.abs(energyConsumption));
        prod.energy = actualEnergy;
    }
    prod.metal *= 1.0 + 0.02 * state.research.metTech;
    prod.crystal *= 1.0 + 0.02 * state.research.crysTech;
    prod.deuterium *= 1.0 + 0.02 * state.research.deutTech;
    prod.deuterium += fusion.deuterium;
    // Matches the real server: basic_income is scaled by resource_multiplier
    // too, not just building output (src/Core/ResourceUpdate.php's
    // ExecCalc: metal_basic_income * (resource_multiplier / 2500)).
    prod = addResources(prod, timesResources(defaultProd, state.ressSpeed));
    return (prod);
  }

  // Helper function to format time
    export const formatTime = (hours) => {
    const totalSeconds = Math.floor(hours * 3600); // Convert hours to total seconds
    
    const days = Math.floor(totalSeconds / (24 * 3600));
    const remainingSecondsAfterDays = totalSeconds % (24 * 3600);
    
    const hrs = Math.floor(remainingSecondsAfterDays / 3600);
    const remainingSecondsAfterHours = remainingSecondsAfterDays % 3600;
    
    const mins = Math.floor(remainingSecondsAfterHours / 60);
    const secs = remainingSecondsAfterHours % 60;
  
    return `${days > 0 ? days + "d " : ""}${hrs > 0 ? hrs + "h " : ""}${mins > 0 ? mins + "m " : ""}${secs}s`;
  };

  export const absoluteTime = (start, hours) => {
    const absDate = new Date(new Date(start).setTime((new Date(start)).getTime() + Math.floor(hours * 60 * 60 * 1000)));
    return absDate.toLocaleString();
  }

  export const calcResourceGatherTime = (state, planetId, cost) => {
    const ress = state.planets[planetId].resources
    const prod = calcProduction(state, planetId);
    if(cost.energy > 0 && cost.energy > prod.energy) return -1;
    cost.metal = Math.max(0, cost.metal - ress.metal);
    cost.crystal = Math.max(0, cost.crystal - ress.crystal);
    cost.deuterium = Math.max(0, cost.deuterium - ress.deuterium);
    if( (cost.metal > 0 && prod.metal <= 0) || 
        (cost.crystal > 0 && prod.crystal <= 0) ||
        (cost.deuterium > 0 && prod.deuterium <= 0))
    {
        return -1;
    }
    const result = Math.max( cost.metal > 0 ? cost.metal / prod.metal : 0, cost.crystal > 0 ? cost.crystal / prod.crystal : 0, cost.deuterium > 0 ? cost.deuterium / prod.deuterium : 0);
    return Math.ceil(result * 3600) / 3600
  }

  export const calcBuildingBuildTime = (state, planetId, cost) => {
    const roboMultiplier = ( 1.0 + state.planets[planetId].buildings.robotFactory);
    const naniteMultiplier = Math.pow( 2.0, state.planets[planetId].buildings.naniteFactory);
    const result = (cost.metal + cost.crystal) * 1.0 / (2500.0 * (state.buildSpeed) * roboMultiplier * naniteMultiplier);
    return Math.round(result * 3600) / 3600
  }

  export const calcResearchBuildTime = (state, planetId, cost, available) => {
    let max_labs = state.research.igrnTech;
    let current_labs = 1
    let lab_level = state.planets[planetId].buildings.researchLab;
    while(current_labs < max_labs)
    {
        let candidate = -1;
        for(const i in state.planets)
        {
            if(i == planetId) continue;
            if(available(state, i))
            {
                if(candidate == -1 || state.planets[i].buildings.researchLab > state.planets[candidate].buildings.researchLab)
                {
                    candidate = i;
                }
            }
        }
        if(candidate == -1) break;
        lab_level += state.planets[candidate].buildings.researchLab;
        current_labs += 1;
    }
    return Math.floor((cost.metal + cost.crystal) * 1.0 / (1000.0 * (state.researchSpeed) * ( 1.0 + lab_level)) * 3600 - 1) / 3600
  }

  export const calcShipBuildTime = (state, planetId, cost) => {
    const hangarMultiplier = ( 1.0 + state.planets[planetId].buildings.hangar);
    const naniteMultiplier = Math.pow( 2.0, state.planets[planetId].buildings.naniteFactory);
    const result = (cost.metal + cost.crystal) * 1.0 / (2500.0 * (state.buildSpeed) * hangarMultiplier * naniteMultiplier);
    return Math.floor(result * 3600) / 3600
  }
  